import { ChevronDown } from "lucide-react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import { SIN_ASIGNAR, type FiltroPersonas } from "../dominio/equipo";

/**
 * Filtro por persona con selección múltiple (Robert, 13/10/2026): Robert, Jan o cualquier
 * combinación, y «Sin asignar». Solo para administradores; un setter ya ve solo lo suyo.
 * Sustituye al «Fichas de» de una sola persona en Leads, Pipeline y Agenda.
 */
export const FiltroPersonasSelect = ({
  valor,
  cambiar,
}: {
  valor: FiltroPersonas;
  cambiar: (v: FiltroPersonas) => void;
}) => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const { usuarios, nombre } = useResponsables();
  if (!esAdmin) return null;

  const opciones = [
    { id: SIN_ASIGNAR, texto: translate("nevora.negocios.sin_asignar") },
    ...usuarios
      .filter((u) => !u.disabled)
      .map((u) => ({ id: String(u.id), texto: nombre(u.id) })),
  ];
  // Solo cuenta lo que sigue existiendo (un usuario borrado no deja el filtro vacío).
  const elegidas = valor.filter((v) => opciones.some((o) => o.id === v));
  const etiqueta = !elegidas.length
    ? translate("nevora.equipo.todas_personas")
    : elegidas.length <= 2
      ? elegidas
          .map((v) => opciones.find((o) => o.id === v)?.texto ?? "")
          .join(", ")
      : translate("nevora.equipo.n_personas", {
          smart_count: elegidas.length,
        });
  const alternar = (id: string, marcada: boolean) =>
    cambiar(
      marcada
        ? [...elegidas.filter((v) => v !== id), id]
        : elegidas.filter((v) => v !== id),
    );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-9 w-56 justify-between font-normal"
          aria-label={`${translate("nevora.equipo.filtro_persona")}: ${etiqueta}`}
          data-testid="filtro-persona"
        >
          <span className="truncate">{etiqueta}</span>
          <ChevronDown className="size-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-60 p-2">
        <fieldset className="space-y-1">
          <legend className="sr-only">
            {translate("nevora.equipo.filtro_persona")}
          </legend>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            disabled={!elegidas.length}
            onClick={() => cambiar([])}
          >
            {translate("nevora.equipo.todas_personas")}
          </Button>
          {opciones.map((o) => (
            <div key={o.id} className="flex items-center gap-2 px-2 py-1.5">
              <Checkbox
                id={`persona-${o.id}`}
                checked={elegidas.includes(o.id)}
                onCheckedChange={(c) => alternar(o.id, c === true)}
              />
              <Label
                htmlFor={`persona-${o.id}`}
                className="font-normal cursor-pointer"
              >
                {o.texto}
              </Label>
            </div>
          ))}
        </fieldset>
      </PopoverContent>
    </Popover>
  );
};
